import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { Document } from '../types/Document';
import { useManagerData, ManagerUserData } from '../hooks/useManagerData';
import { useCompanyDocumentData } from '../hooks/useCompanyDocumentData';
import { useUserDetailsApi } from '../hooks/useUserDetails';

interface ManagerContextType {
	agencyUsers: ManagerUserData[];
	agencyDocumentMap: any;
	companyDocuments: Document[];
	companyFieldValues: { [key: string]: any };
	// Flattened company structure data
	companyCategories: any[];
	companySubcategories: any[];
	companyDocumentTypes: any[];
	companyDocumentFields: any[];
	roles: any[];
	// Company completion stats
	companyCategoriesWithStats: any[];
	companyOverallStats: {
		totalItems: number;
		completedItems: number;
		missingItems: number;
		expiredItems: number;
		completionRate: number;
	};
	// Helper methods for company documents
	getCompanySubcategoriesForCategory: (categoryId: string) => any[];
	getCompanyDocumentTypesForSubcategory: (subcategoryId: string) => any[];
	getCompanyDocumentTypesForCategory: (categoryId: string) => any[];
	getCompanyFieldsForSubcategory: (subcategoryId: string) => any[];
	getCompanyFieldsForCategory: (categoryId: string) => any[];
	// Company document operations
	companyDocumentUpload: (formData: FormData) => Promise<{ success: boolean; error?: string; newDocument?: any }>;
	companyDocumentRemove: (docID: string) => Promise<{ success: boolean; error?: string }>;
	// Company field management functions
	saveCompanyFieldValues: (values: { [key: string]: any }) => Promise<any>;
	fetchCompanyFieldValues: (fieldIds: string[]) => void | Promise<void>;
	// Agency management functions
	sendUserInvitation: (inviteData: { email: string; agencyId: string; role: string; senderEmail: string }) => Promise<any>;
	removeAgencyUsers: (agencyId: string, userIds: string[], userId: string) => Promise<any>;
	updatePaymentStatus: () => Promise<any>;
	loading: boolean;
	error: string | null;
	fetchAgencyUsersData: (agencyId: string) => Promise<void>;
	refetch: () => void;
}

const ManagerContext = createContext<ManagerContextType | undefined>(undefined);

export const useManagerContext = () => {
	const ctx = useContext(ManagerContext);
	if (!ctx) throw new Error('useManagerContext must be used within a ManagerProvider');
	return ctx;
};

interface ManagerProviderProps {
	children: ReactNode;
}

export const ManagerProvider: React.FC<ManagerProviderProps> = ({ children }) => {
	const { 
		agencyUsers, 
		agencyDocumentMap, 
		companyDocuments, 
		companyFieldValues,
		companyCategories,
		companySubcategories,
		companyDocumentTypes,
		companyDocumentFields,
		roles,
		getCompanySubcategoriesForCategory,
		getCompanyDocumentTypesForSubcategory,
		getCompanyDocumentTypesForCategory,
		getCompanyFieldsForSubcategory,
		getCompanyFieldsForCategory,
		companyDocumentUpload,
		companyDocumentRemove,
		saveCompanyFieldValues,
		fetchCompanyFieldValues,
		loading, 
		error, 
		fetchAgencyUsersData, 
		refetch 
	} = useManagerData();
	
	// Get agency management functions from useUserDetailsApi
	const { sendUserInvitation, removeAgencyUsers, updatePaymentStatus } = useUserDetailsApi();

	// Calculate company document completion stats
	const companyData = useCompanyDocumentData({
		categories: companyCategories,
		subcategories: companySubcategories,
		documentTypes: companyDocumentTypes,
		documentFields: companyDocumentFields,
		companyDocuments,
		companyFieldValues
	});

	// Extract these values to avoid property access in dependency array
	const companyCategoriesWithStats = companyData.categoriesWithStats;
	const companyOverallStats = companyData.overallStats;

	// Memoize the context value to prevent unnecessary rerenders
	const contextValue = useMemo(() => ({
		agencyUsers, 
		agencyDocumentMap, 
		companyDocuments, 
		companyFieldValues,
		companyCategories,
		companySubcategories,
		companyDocumentTypes,
		companyDocumentFields,
		roles,
		companyCategoriesWithStats,
		companyOverallStats,
		getCompanySubcategoriesForCategory,
		getCompanyDocumentTypesForSubcategory,
		getCompanyDocumentTypesForCategory,
		getCompanyFieldsForSubcategory,
		getCompanyFieldsForCategory,
		companyDocumentUpload,
		companyDocumentRemove,
		saveCompanyFieldValues,
		fetchCompanyFieldValues,
		sendUserInvitation,
		removeAgencyUsers,
		updatePaymentStatus,
		loading, 
		error, 
		fetchAgencyUsersData, 
		refetch,
	}), [
		agencyUsers, 
		agencyDocumentMap, 
		companyDocuments, 
		companyFieldValues,
		companyCategories,
		companySubcategories,
		companyDocumentTypes,
		companyDocumentFields,
		roles,
		companyCategoriesWithStats,
		companyOverallStats,
		loading, 
		error,
	]);
	
	return (
		<ManagerContext.Provider value={contextValue}>
			{children}
		</ManagerContext.Provider>
	);
};
